import { type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PERFORMANCE_PAYMENT_SUBPRODUCT_KEY } from "@/lib/brokerBonds";
import { bidResultLockReason, offersBidResult } from "@/lib/bidResult";
import { useBondOrder } from "@/lib/useBondOrder";
import { useSubproductIdByKey } from "@/lib/usePreapprovalSubproduct";
import type { WonSubmittedState } from "@/lib/submittedState";
import { BidResultRecord } from "@/components/BidResultRecord";
import { PageHeader } from "@/components/PageHeader";
import { WonBidStepper } from "@/components/bond-submission/WonBidStepper";

// BRK-S184 — the won-bid screen (`/surety/submissions/:id/won`), where a win
// is recorded with bid result v2's body (PMS-S488). Since BRK-S421 it HOSTS
// the won-bid stepper — the same four steps the Bid Result dialog's Won
// opens — so the bid result screen's Continue, deep links and the reminders
// keep landing on one flow. The page keeps what only a route needs: its own
// read of the order, the guards (no bid result on this request; a locked
// result reads read-only), and the hand-off to the detail page's banner,
// which reads the RESPONSE's `workflowStatus`, never a preview (NN #7).

export function WonBidPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { order, setOrder, error: readError } = useBondOrder(id);
  const ppSubproductId = useSubproductIdByKey(
    PERFORMANCE_PAYMENT_SUBPRODUCT_KEY,
  );
  if (readError) {
    return (
      <Shell>
        <p role="alert" className="text-sm text-brk-danger">
          {readError}
        </p>
        <BackLink id={id} />
      </Shell>
    );
  }
  if (!order || ppSubproductId === undefined) {
    return (
      <Shell>
        <p role="status" className="text-sm text-brk-dim">
          Loading…
        </p>
      </Shell>
    );
  }

  const saved = order.bidResult ?? null;
  const lockReason = saved ? bidResultLockReason(order) : null;
  const header = (
    <PageHeader
      title="Won bid"
      meta={
        <span className="break-words">
          <span className="tabular-nums">{order.number}</span>
          {order.accountLegalName ? ` · ${order.accountLegalName}` : ""}
        </span>
      }
    />
  );

  if (!offersBidResult(order, ppSubproductId)) {
    return (
      <Shell header={header}>
        <p
          role="status"
          className="rounded-xl border border-brk-border bg-brk-panel p-4 text-sm text-brk-text sm:p-6"
        >
          There’s no bid result to record on this request.
        </p>
        <BackLink id={order.id} />
      </Shell>
    );
  }

  if (saved && lockReason !== null) {
    return (
      <Shell header={header}>
        <Panel title="Recorded result" id="won-recorded">
          <BidResultRecord result={saved} ourBid={saved.ourBidAmount} />
          <p className="text-sm text-brk-dim">{lockReason}</p>
        </Panel>
        <BackLink id={order.id} />
      </Shell>
    );
  }

  return (
    <Shell header={header}>
      <WonBidStepper
        order={order}
        onSaved={setOrder}
        onRecorded={(fresh, bondsRequested) => {
          // The banner's sentence comes from the RESPONSE (NN #7).
          const state: WonSubmittedState = {
            wonSubmitted: true,
            status: fresh.status,
            bondsRequested,
          };
          navigate(`/surety/submissions/${order.id}`, { replace: true, state });
        }}
      />
      <Link
        to={`/surety/submissions/${order.id}/bid-result`}
        className="text-sm text-brk-accent hover:underline"
      >
        ← Back to the bid result
      </Link>
    </Shell>
  );
}

function Panel({
  title,
  id,
  children,
}: {
  title: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <h2 id={id} className="text-heading text-brk-text">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** The page's one header (BRK-S262's PageHeader) over its body, on the
 *  shell's padding like every portal page. */
function Shell({
  header = <PageHeader title="Won bid" />,
  children,
}: {
  header?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      {header}
      <section className="flex min-w-0 flex-col gap-6">{children}</section>
    </>
  );
}

function BackLink({ id }: { id: string }) {
  return (
    <Link
      to={`/surety/submissions/${id}`}
      className="text-sm text-brk-accent hover:underline"
    >
      ← Back to the request
    </Link>
  );
}
